/* eslint-disable max-len */
import React, { CSSProperties, useContext, useEffect, useState } from 'react';
import { useNavigate, RouteProps, useParams, Navigate } from 'react-router-dom';
import { Agent } from '@moderepo/mode-internal-apis';
import { ritzDataContext, selectAgentAuth, selectLoggedInAgent } from '@moderepo/mode-data-state-ritz';
import { deleteLocalStorageItem, LoadingScreen } from '@moderepo/mode-app-components';
import { PageContainer } from '../page/PageContainer';
import { Layout } from '../layout/Layout';
import { RouteHelper } from './RouteHelper';
import * as Constants from '../../utils/constants';
import { loadRequiredData, RouteRequiredData } from './RouteDataHelper';
import { redirectUserToAnotherOrganization } from '../../utils/utils';
import { getOrganization } from '../app/App';


/**
 * RitzRoute can be used for page that required the agent to be authenticated, pages that required the agent NOT to be authenticated or pages
 * that does not care if the agent is authenticated. So when we use RitzRoute, we need to specified if the route require login.
 *
 * requiredLogin: Set to true to SPECIFICALLY indicate that the route require the agent to be logged in.
 *                Set to false to SPECIFICALLY indicate that the route required that the agent is NOT logged in.
 *                Set to undefined or unset to indicate that it doesn't matter if the agent is logged in or not. NOTE that setting it to 'false'
 *                is NOT the same as setting it to 'undefined'. 'undefined' means we don't care while 'false' mean the agent MUST NOT be logged in
 *                to enter this route.
 */
export interface RitzRouteProps extends RouteProps {
    readonly pageProps?: {
        readonly className?: string | undefined;
        readonly style?: CSSProperties | undefined;
    } | undefined;
    readonly layoutProps?: unknown| undefined;
    readonly requiredLogin?: boolean | undefined;
}



/**
 * This is an extension of the react's Route. Unlike React's Route, this Route will take care of loading the required data BEFORE displaying
 * the UI. This route will also take care of redirecting the agent to login or other pages accordingly if the route required login but the
 * agent is not logged in.
 */
export const RitzRoute: React.FC<RitzRouteProps> = (props: RitzRouteProps) => {
    // Get the 'element' and requiredPermissions array from the route's props
    // and everything else goes into the 'otherProps' array
    const {
        element: Element,
        layoutProps,
        pageProps,
    } = props;

    const params = useParams();
    const navigate = useNavigate();
    const projectId = params.projectId ? Number(params.projectId) : undefined;
    const userId = params.userId ? Number(params.userId) : undefined;

    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const auth = selectAgentAuth(dataState);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const routeHelper = RouteHelper.getInstance(navigate);
    const organization = getOrganization();


    // Keep track of the current page pathname. If this pathname changed, we know the user moved to a different page so we can
    // update the requiredData accordingly
    const [requiredData, setRequiredData] = useState<RouteRequiredData | undefined>();



    useEffect(() => {
        const loadData = async () => {
            const data = await loadRequiredData(
                projectId, userId, dataDispatch, auth.agentLoginInfo, auth.loggedInAgent, undefined, undefined, undefined, undefined, undefined,
            );
            setRequiredData(data);
        };

        loadData();
    }, [auth.agentLoginInfo, auth.loggedInAgent, dataDispatch, projectId, userId]);


    // If there is no organization, that mean we weren't able get the organization based on the baseUrl or the orgId in the path.
    if (!organization) {
        return (
            <Navigate to={routeHelper.getNotAuthorizedPageUrl()} />
        );
    }


    if (!requiredData) {
        return (
            <LoadingScreen />
        );
    }


    // LOADING DATA COMPLETED. Now we can check for required data and display the page if everything is good

    if (props.requiredLogin === false && loggedInAgent) {
        // Because this Route is SPECIFICALLY set to NOT require login but the agent is logged in already then we need to take the user to the
        // home page instead.
        return (
            <Navigate to={routeHelper.getRitzHomePageUrl()} />
        );
    }

    if (props.requiredLogin === true && !loggedInAgent) {
        // This page SPECIFICALLY set to require login but the user is NOT logged in so we need to take the user to the login page.
        // Before redirect the agent, we need to clear all login info from localStorage and RitzAPI
        deleteLocalStorageItem(Constants.LS_KEY_LOGIN_INFO);

        return (
            <Navigate to={routeHelper.getLoginPageUrl()} />
        );
    }


    /**
     * If the user tries to enter a page of an organization that doesn't match the agent's current logged in organization, then we will
     * redirect the user to their logged in organization.
     */
    if (loggedInAgent && loggedInAgent.orgId !== organization?.id) {
        redirectUserToAnotherOrganization(organization.id, loggedInAgent.orgId);
        // Return loading screen so that we don't continue doing anything else while we redirect
        return (
            <LoadingScreen />
        );
    }


    // Everything passed the check, now we can display the page.
    return (
        <PageContainer projectId={projectId} layoutComponent={Layout} {...pageProps} layoutProps={layoutProps}>
            {/* <Element {...componentProps} /> */}
            {Element}
        </PageContainer>
    );
};
